'use client'

import { useState, useRef, DragEvent } from 'react'
import {
    MdUploadFile,
    MdLock,
    MdClose,
    MdInsertDriveFile,
    MdCheckCircle,
} from 'react-icons/md'
import {
    uploadCertificateA1,
    DigitalCertificate,
} from '@/lib/api/medical/signature-api'
import { toast } from 'react-toastify'

interface CertificateUploadProps {
    professionalId: number
    onUploaded?: (certificate: DigitalCertificate) => void
    onCancel?: () => void
}

export function CertificateUpload({
    professionalId,
    onUploaded,
    onCancel,
}: CertificateUploadProps) {
    const [file, setFile] = useState<File | null>(null)
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [dragOver, setDragOver] = useState(false)
    const [result, setResult] = useState<DigitalCertificate | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const acceptedTypes = ['.pfx', '.p12']

    const isValidFile = (f: File): boolean => {
        const ext = f.name.toLowerCase()
        return acceptedTypes.some((t) => ext.endsWith(t))
    }

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    const handleFileSelect = (f: File) => {
        if (!isValidFile(f)) {
            toast.error(
                'Formato inválido. Selecione um arquivo .pfx ou .p12'
            )
            return
        }
        if (f.size > 10 * 1024 * 1024) {
            toast.error('O arquivo deve ter no máximo 10MB')
            return
        }
        setFile(f)
    }

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setDragOver(false)
        const f = e.dataTransfer.files[0]
        if (f) handleFileSelect(f)
    }

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setDragOver(true)
    }

    const handleDragLeave = () => {
        setDragOver(false)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0]
        if (f) handleFileSelect(f)
    }

    const handleUpload = async () => {
        if (!file) {
            toast.error('Selecione o arquivo do certificado')
            return
        }
        if (!password.trim()) {
            toast.error('Informe a senha do certificado')
            return
        }

        setLoading(true)
        try {
            const response = await uploadCertificateA1(
                professionalId,
                file,
                password
            )

            if (response.success && response.result) {
                setResult(response.result)
                toast.success('Certificado registrado com sucesso!')
                onUploaded?.(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao registrar certificado'
                )
            }
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ||
                    'Erro ao registrar certificado. Verifique o arquivo e a senha.'
            )
        } finally {
            setLoading(false)
        }
    }

    const handleClear = () => {
        setFile(null)
        setPassword('')
        setResult(null)
        if (inputRef.current) inputRef.current.value = ''
    }

    if (result) {
        return (
            <div className="space-y-4">
                <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <MdCheckCircle className="text-3xl text-green-600 shrink-0" />
                    <div>
                        <p className="font-bold text-green-700 dark:text-green-400">
                            Certificado registrado com sucesso!
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-500">
                            O certificado foi validado e armazenado de forma
                            segura.
                        </p>
                    </div>
                </div>

                <div className="bg-base-200 rounded-lg p-3 space-y-1 text-sm">
                    <p>
                        <span className="font-medium">Titular:</span>{' '}
                        {result.subject}
                    </p>
                    <p>
                        <span className="font-medium">Emissor:</span>{' '}
                        {result.issuer}
                    </p>
                    <p>
                        <span className="font-medium">
                            Número de série:
                        </span>{' '}
                        {result.serialNumber}
                    </p>
                    <p>
                        <span className="font-medium">Validade:</span>{' '}
                        {new Date(result.validFrom).toLocaleDateString(
                            'pt-BR'
                        )}{' '}
                        a{' '}
                        {new Date(result.validUntil).toLocaleDateString(
                            'pt-BR'
                        )}
                    </p>
                </div>

                <div className="flex justify-end">
                    <button
                        className="btn btn-ghost"
                        onClick={onCancel}
                    >
                        Fechar
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {/* Drop zone */}
            <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    dragOver
                        ? 'border-secondary bg-secondary/5'
                        : file
                          ? 'border-success bg-success/5'
                          : 'border-base-300 hover:border-secondary/50'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => inputRef.current?.click()}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".pfx,.p12"
                    onChange={handleInputChange}
                    className="hidden"
                />

                {file ? (
                    <div className="flex items-center justify-center gap-3">
                        <MdInsertDriveFile className="text-3xl text-success" />
                        <div className="text-left">
                            <p className="font-medium text-base-content">
                                {file.name}
                            </p>
                            <p className="text-sm text-base-content/60">
                                {formatFileSize(file.size)}
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm btn-square"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleClear()
                            }}
                        >
                            <MdClose className="text-lg" />
                        </button>
                    </div>
                ) : (
                    <>
                        <MdUploadFile className="text-4xl text-base-content/30 mx-auto mb-2" />
                        <p className="font-medium text-base-content">
                            Arraste o certificado aqui ou clique para
                            selecionar
                        </p>
                        <p className="text-sm text-base-content/50 mt-1">
                            Formatos aceitos: .pfx, .p12 (máximo 10MB)
                        </p>
                    </>
                )}
            </div>

            {/* Senha */}
            <div className="form-control w-full">
                <label className="label">
                    <span className="label-text font-medium">
                        Senha do Certificado
                    </span>
                </label>
                <div className="relative">
                    <input
                        type="password"
                        className="input input-bordered w-full pr-10"
                        placeholder="Digite a senha do certificado"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && file && password.trim())
                                handleUpload()
                        }}
                        disabled={loading}
                    />
                    <MdLock className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30" />
                </div>
                <label className="label">
                    <span className="label-text-alt text-base-content/50">
                        A senha é necessária para validar e extrair os dados
                        do certificado
                    </span>
                </label>
            </div>

            {/* Ações */}
            <div className="flex justify-end gap-2">
                {onCancel && (
                    <button
                        className="btn btn-ghost"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                )}
                <button
                    className="btn btn-secondary"
                    onClick={handleUpload}
                    disabled={loading || !file || !password.trim()}
                >
                    {loading ? (
                        <span className="loading loading-spinner loading-sm" />
                    ) : (
                        <MdUploadFile className="text-lg" />
                    )}
                    {loading ? 'Registrando...' : 'Registrar Certificado'}
                </button>
            </div>
        </div>
    )
}
